import React, { useEffect, useRef, useState, useContext } from 'react';
import _ from 'lodash';
import {
  asPercentageFixed,
  asPercentageRounded,
  numberWithCommas,
} from 'spw-shared/utils';
import {
  JustifyStartRow,
  PaddingLeft8,
  PaddingRight8,
  SpaceBetweenRow,
} from 'spw-shared/globalComponents/grid';
import { Box } from '@mui/material';
import { FullWidthRow } from 'spw-components/floorplan/styles';
import { useSelector } from 'react-redux';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import {
  CUSTOM_PREFIX,
  EXECUTIVE_PRIVATE_OFFICE,
  SHARED_PRIVATE_OFFICE,
  SPACE_NAME_MAP,
} from 'spw-constants';
import { CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH } from 'spw-programming-projects/constants';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import {
  DEFAULT_DUPLICATE_CUSTOM_SPACE,
  shouldDisplayAsRatio,
} from 'spw-components/worksheet/utils';
import {
  ArrowContainer,
  HeaderValue,
  SpaceTypeNameContainer,
  WorksheetInput,
} from 'spw-components/worksheet/styles';
import {
  GridColumn,
  GridRow,
} from 'spw-programming-projects/building-details/worksheet/styles';
import { WorksheetContext } from 'spw-programming-projects/building-details/worksheet/worksheet-context';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import { Totals } from '../../../../models/Worksheet';
import WorksheetRow from '../../../../models/WorksheetRow';
import ChevronDown from '../../../../../static/images/chevron-down-outlined.svg';
import ChevronUp from '../../../../../static/images/chevron-up-outlined.svg';

const { MEETING_SPACES } = SPACE_NAME_MAP;

interface ParentSpaceTypeRowProps {
  name: string;
  row: WorksheetRow;
  totalCategorySeats: number;
  collapsed?: boolean;
  setCollapsed?: () => void;
  renameSpaceType?: (
    row: WorksheetRow,
    name: string,
    isCustomSpaceType?: boolean
  ) => boolean;
  active: boolean;
  disabled?: boolean;
  showDecimals: boolean;
  spaceTypeTotals: Totals;
  belongsToTotalProgram: boolean;
  shouldDisplayRowValues?: boolean;
  isPerDepartmentParentRow?: boolean;
  clientFacingNamesAreVisible: boolean;
  isDraggable?: boolean;
}

export const breakLine = (rowName: string | JSX.Element) => {
  if (typeof rowName !== 'string') return rowName;
  // Hardcoded for SPW-1703
  const seatsDescRegex = /Room \(.*? seats\)/;
  const seatsRangeRegex = /\(.*? seats\)/;
  const nameWithoutlineBreaks = rowName.replace(/\n/g, '');
  let seatsDescPosition = nameWithoutlineBreaks.slice().search(seatsDescRegex);
  if (seatsDescPosition < 10 && seatsDescPosition > 0) {
    seatsDescPosition = nameWithoutlineBreaks.slice().search(seatsRangeRegex);
  }
  if (seatsDescPosition !== -1) {
    return (
      <span>
        {nameWithoutlineBreaks.slice(0, seatsDescPosition)}
        <br />
        {nameWithoutlineBreaks.slice(seatsDescPosition)}
      </span>
    );
  }

  return rowName;
};

export const textAreaReachedMaxRows = (
  textArea: HTMLTextAreaElement,
  previousValue?: string
) => {
  const lineHeight = 16;
  const textContentHeight = textArea.scrollHeight;
  const numberOfLines = Math.floor(textContentHeight / lineHeight);
  const spaceTypeNameMaxLines = 2;

  return (
    numberOfLines > spaceTypeNameMaxLines &&
    textArea.value.length >= previousValue.length
  );
};

export const ParentSpaceTypeRow: React.FC<ParentSpaceTypeRowProps> = ({
  name,
  row,
  totalCategorySeats,
  setCollapsed,
  collapsed,
  renameSpaceType,
  active,
  disabled,
  showDecimals,
  spaceTypeTotals,
  belongsToTotalProgram,
  shouldDisplayRowValues,
  isPerDepartmentParentRow,
  clientFacingNamesAreVisible,
  isDraggable,
}) => {
  const { convertToUnit } = useUnitConversion();
  const [isHoveringName, setIsHoveringName] = useState(false);
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);

  const isRatio = shouldDisplayAsRatio(
    row.projectSpaceTypeCategory,
    row.projectSpaceTypeSubcategory
  );

  const { onRowNameFocus, onRowNameBlur } = useContext(WorksheetContext);

  const arrowUpComponent =
    isPerDepartmentParentRow && row.child ? (
      <KeyboardArrowUp fontSize="small" />
    ) : (
      <img src={ChevronUp} width={15} alt="collapse" />
    );
  const arrowDownComponent =
    isPerDepartmentParentRow && row.child ? (
      <KeyboardArrowDown fontSize="small" />
    ) : (
      <img src={ChevronDown} width={15} alt="expand" />
    );

  const rowRef = useRef(null);
  const isIndividualOrOffice = isIWCategoryOrSubcategory(
    row.projectSpaceTypeCategory
  );

  // SPW-2108 For rendering the TPS we use this component (despite being called parent-space-type-row)
  // to store the "child" or nested rows. This is because the lowest level rows are linked department level
  const testingSuffix = row.child
    ? `child_${row.spaceType.id}`
    : row.spaceType.id;

  const isCustom = (row.spaceType?.spaceType as string)?.includes(
    CUSTOM_PREFIX
  );
  const [editValue, setEditValue] = useState('');
  const [editFactor, setEditFactor] = useState('');
  const [tempName, setTempName] = useState(null);

  const clientFacingName = row.projectSpaceType?.clientFacingName;

  // Sets the temporary name every time a space type row is renamed
  useEffect(() => {
    setTempName(
      clientFacingNamesAreVisible && clientFacingName ? clientFacingName : name
    );
  }, [name, clientFacingNamesAreVisible, clientFacingName]);

  useEffect(() => {
    if (!clientFacingNamesAreVisible) {
      setIsHoveringName(false);
    }
  }, [clientFacingNamesAreVisible]);

  // manually blur inputs on enter
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      (e.target as HTMLInputElement).blur();
    }
  };
  let displayName: string | JSX.Element = name;

  if (
    row.spaceType.spaceType === MEETING_SPACES.PHONE_ROOM ||
    row.spaceType.spaceType === MEETING_SPACES.HUDDLE_ROOM
  ) {
    const index = name.indexOf('(');
    const firstName = name.slice(0, index - 1);
    const lastName = name.slice(index);
    displayName = (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        flexDirection="column"
      >
        <span>{firstName}</span>
        <span>{lastName}</span>
      </Box>
    );
  }
  // need to hard code how these names display.
  // If anything ever changes with how we display row names, request a spec for these specific cases

  if (row.spaceType.spaceType === SHARED_PRIVATE_OFFICE) {
    displayName = (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        flexDirection="column"
      >
        <span>Shared</span>
        <span>Private Office</span>
      </Box>
    );
  }

  if (row.spaceType.spaceType === EXECUTIVE_PRIVATE_OFFICE) {
    displayName = (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        flexDirection="column"
      >
        <span>Executive</span>
        <span>Private Office</span>
      </Box>
    );
  }

  if (row.spaceType.spaceType === MEETING_SPACES.MEDIUM_CONFERENCE_ROOM) {
    displayName = 'Med. Conference Room (7-10 seats)';
  }

  const [hover, setHover] = useState(false);

  const inputRef: { current: HTMLElement } = React.useRef();

  const { spacesQuantity, seats, ratio, USF, seatMix } = spaceTypeTotals || {
    seats: row.quantity * row.seatCap || 0,
    spacesQuantity: row.quantity || 0,
    ratio: row.ratio || 0,
    USF: row.totalUsf || 0,
    seatMix: row.ratio || 0,
  };

  let className =
    isIWCategoryOrSubcategory(row.projectSpaceTypeCategory) ||
    belongsToTotalProgram
      ? ''
      : 'draggable';

  if (editFactor !== '') {
    className = className.concat(' active');
  } else if (hover || active) {
    className = className.concat(' hover');
  }

  const onFocusRowName = (e: React.BaseSyntheticEvent) => {
    if (
      (editFactor !== 'name' || editValue === DEFAULT_DUPLICATE_CUSTOM_SPACE) &&
      !disabled
    ) {
      e.stopPropagation();
      onRowNameFocus();

      const timeout = setTimeout(() => {
        const currentInput = inputRef?.current;
        if (currentInput) {
          currentInput.focus();
        }
      }, 100);
      if (clientFacingNamesAreVisible && clientFacingName) {
        setEditValue(clientFacingName);
      } else if (tempName) {
        setEditValue(tempName);
      } else {
        setEditValue('');
      }

      setEditFactor(`name`);

      return () => {
        clearTimeout(timeout);
      };
    }

    return _.noop();
  };

  const onBlurWorksheetInput = () => {
    onRowNameBlur();
    if (renameSpaceType) {
      if (
        (editValue.trim() && editValue !== tempName) ||
        (clientFacingNamesAreVisible && !isCustom)
      ) {
        const nameIsDuplicated = !renameSpaceType(
          row,
          editValue.trim(),
          isCustom
        );

        if (
          editValue.toLowerCase() ===
            DEFAULT_DUPLICATE_CUSTOM_SPACE.toLowerCase() ||
          nameIsDuplicated
        ) {
          setEditValue(DEFAULT_DUPLICATE_CUSTOM_SPACE);
        } else {
          if (editValue.trim()) setTempName(editValue.trim());
          setEditFactor('');
          setEditValue('');
        }
      } else {
        setEditFactor('');
        setEditValue('');
      }
    }
  };

  let totalSeatsPercentage = showDecimals ? '0.00%' : '0%';
  if (seats > 0) {
    totalSeatsPercentage = showDecimals
      ? asPercentageFixed(seats / totalCategorySeats)
      : asPercentageRounded(seats / totalCategorySeats);
  }

  if (row.removed) return <div />;

  const rowName =
    clientFacingNamesAreVisible && clientFacingName
      ? clientFacingName
      : tempName || displayName;

  return (
    <div
      ref={rowRef}
      style={{
        // 80px for the worksheet header
        // then, we wanted to give a little more context to the user about where they are to avoid confusion.
        // 120px is the category header (80) + gray subheader (40)
        scrollMarginTop: '200px',
      }}
      data-cy={`parent_space_type_row_${rowName}`}
    >
      <GridRow
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`${className} ${
          isDraggable && !currentUserIsViewOnly ? 'draggable' : ''
        }`}
      >
        <GridColumn xs={3} className="row-start">
          <FullWidthRow>
            <SpaceBetweenRow style={{ width: '100%' }}>
              <JustifyStartRow width="100%">
                <ArrowContainer onClick={setCollapsed}>
                  {collapsed ? arrowDownComponent : arrowUpComponent}
                </ArrowContainer>

                <div
                  style={{
                    width: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
                    minWidth: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
                  }}
                >
                  {editFactor === 'name' ? (
                    <WorksheetInput
                      inputRef={inputRef}
                      onKeyPress={handleKeyPress}
                      className={`left-align ${
                        editValue.toLowerCase() ===
                        DEFAULT_DUPLICATE_CUSTOM_SPACE.toLowerCase()
                          ? 'duplicated'
                          : ''
                      }`}
                      multiline
                      variant="outlined"
                      value={editValue}
                      onFocus={onFocusRowName}
                      onChange={(e) => {
                        setEditValue(
                          textAreaReachedMaxRows(
                            e.target as HTMLTextAreaElement,
                            editValue
                          )
                            ? editValue
                            : e.target.value
                        );
                      }}
                      onBlur={onBlurWorksheetInput}
                    />
                  ) : (
                    <SpaceTypeNameContainer
                      className={
                        isHoveringName && !belongsToTotalProgram ? 'hover' : ''
                      }
                      onMouseEnter={(e) => {
                        if (clientFacingNamesAreVisible || isCustom) {
                          setIsHoveringName(true);
                          e.stopPropagation();
                        }
                      }}
                      onMouseLeave={() => setIsHoveringName(false)}
                      onClick={(e: React.BaseSyntheticEvent) => {
                        e.stopPropagation();
                        if (
                          (isCustom ||
                            clientFacingNamesAreVisible ||
                            row.child) &&
                          !currentUserIsViewOnly
                        ) {
                          onFocusRowName(e);
                        }
                      }}
                      width="100%"
                      $disablePointer={currentUserIsViewOnly}
                      data-cy={`worksheet_row_space_type_name_${name}`}
                      data-testid={`worksheet_row_name_${testingSuffix}`}
                    >
                      <div
                        style={{
                          maxHeight: 32,
                          overflow: 'hidden',
                        }}
                      >
                        <pre
                          style={{
                            maxHeight: 32,
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            whiteSpace: 'pre-wrap',
                            display: 'inline-flex',
                          }}
                        >
                          {breakLine(tempName)}
                        </pre>
                      </div>
                    </SpaceTypeNameContainer>
                  )}
                </div>
              </JustifyStartRow>
            </SpaceBetweenRow>
          </FullWidthRow>
        </GridColumn>
        <GridColumn
          xs={1}
          style={
            isIndividualOrOffice && !isRatio
              ? {}
              : {
                  justifyContent: 'flex-start',
                  paddingLeft: '8px',
                }
          }
        >
          {isRatio ? (
            <PaddingLeft8>
              <HeaderValue data-testid={`worksheet_row_ratio_${testingSuffix}`}>
                {`1:${numberWithCommas(ratio, showDecimals, true)}`}
              </HeaderValue>
            </PaddingLeft8>
          ) : (
            <PaddingRight8>
              <HeaderValue data-testid={`worksheet_row_ratio_${testingSuffix}`}>
                {`${numberWithCommas(seatMix, showDecimals, true)}%`}
              </HeaderValue>
            </PaddingRight8>
          )}
        </GridColumn>
        <GridColumn xs={1}>
          <HeaderValue
            style={{ paddingRight: '6px' }}
            data-testid={`worksheet_row_qty_${testingSuffix}`}
          >
            {numberWithCommas(spacesQuantity, showDecimals, true)}
          </HeaderValue>
        </GridColumn>
        <GridColumn
          xs={1}
          data-testid={`worksheet_row_seatcap_${testingSuffix}`}
        >
          <PaddingRight8>
            {shouldDisplayRowValues
              ? numberWithCommas(row.seatCap, showDecimals, true)
              : null}
          </PaddingRight8>
        </GridColumn>

        <GridColumn
          xs={1}
          data-testid={`worksheet_row_rowtotal_${testingSuffix}`}
        >
          {numberWithCommas(seats, showDecimals, true)}
        </GridColumn>
        <GridColumn
          xs={2}
          className="seat-percent-column"
          data-testid={`worksheet_row_seatpercent_${testingSuffix}`}
        >
          {totalSeatsPercentage}
        </GridColumn>
        <GridColumn xs={1} />
        <GridColumn xs={1} className="justifyCenter" />
        <GridColumn xs={1} data-testid={`worksheet_row_usf_${testingSuffix}`}>
          <PaddingRight8>
            {shouldDisplayRowValues
              ? convertToUnit(row.usf, {
                  unitType: 'area',
                  formatter: (v) => numberWithCommas(v, showDecimals, true),
                })
              : null}
          </PaddingRight8>
        </GridColumn>
        <GridColumn
          xs={1}
          className="row-end"
          data-testid="row-usf"
          data-cy={`worksheet_row_totalusf_${testingSuffix}`}
        >
          {convertToUnit(USF, {
            unitType: 'area',
            formatter: (v) => numberWithCommas(v, showDecimals, true),
          })}
        </GridColumn>
      </GridRow>
    </div>
  );
};

export default ParentSpaceTypeRow;
